import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, OnDestroy, OnInit } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { environment } from '@env';
import { IModalComponent } from '@lib/modal';
import { PtmSignerApiProviderService } from '@project/data-providers';
import { HcService, LogoutService } from '@project/services';
import { RoutesBuilderService } from '@app/config';
import { extractQueryParam } from '@core/helpers';

type ValidationStep = 'action-required' | 'certificate-instructions' | 'qr-code' | 'processing' | 'error';

const ERROR_COUNTDOWN_SECONDS = 5;

@Component({
  selector: 'app-modal-physician-signature-validation',
  templateUrl: './modal-physician-signature-validation.component.html',
  styleUrls: ['./modal-physician-signature-validation.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalPhysicianSignatureValidationComponent implements IModalComponent<void>, OnInit, OnDestroy {
  public close$ = new EventEmitter<void>();
  public submit$ = new EventEmitter<void>();

  public qrCodeUrl: string | null = null;
  public doctorId: string | null = null;
  public initialCode: string | null = null;
  public missingCertificate = false;
  public blockWorkspaceOnCancel = false;
  public redirectsToLogin = false;
  public certificateStillInvalid = false;

  public step: ValidationStep = 'action-required';
  public safeQrCodeUrl: SafeResourceUrl | null = null;
  public countdown = ERROR_COUNTDOWN_SECONDS;
  public readonly appVersion = environment.appVersion;
  public readonly logoSrc = this.hcService.getHcConfig().assets.logo;
  public readonly vidaasAppleStoreUrl = 'https://apps.apple.com/br/app/vidaas/id1490636157';
  public readonly vidaasGooglePlayUrl = 'https://play.google.com/store/apps/details?id=br.com.valid.vidaas';

  private countdownIntervalId: number | null = null;

  constructor(
    private cdr: ChangeDetectorRef,
    private sanitizer: DomSanitizer,
    private router: Router,
    private ptmSignerApiProviderService: PtmSignerApiProviderService,
    private hcService: HcService,
    private logoutService: LogoutService,
  ) {}

  ngOnInit(): void {
    if (this.missingCertificate) {
      this.step = 'certificate-instructions';
      this.cdr.markForCheck();
      return;
    }

    if (this.qrCodeUrl) {
      this.safeQrCodeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.qrCodeUrl);
    }

    if (this.initialCode) {
      this.confirmValidation(this.initialCode);
    }
  }

  ngOnDestroy(): void {
    this.stopCountdown();
  }

  public get formattedCountdown(): string {
    return `00:${('0' + this.countdown).slice(-2)}`;
  }

  public validateWithQrCode(): void {
    if (!this.qrCodeUrl) {
      return;
    }

    this.step = 'qr-code';
    this.cdr.markForCheck();
  }

  public confirmCertificateRegistration(): void {
    if (!this.doctorId) {
      this.startErrorCountdown();
      return;
    }

    this.step = 'processing';
    this.certificateStillInvalid = false;
    this.cdr.markForCheck();

    this.ptmSignerApiProviderService.validateDoctorCertificate(this.doctorId).subscribe(
      (validation) => {
        if (!validation.cert) {
          this.certificateStillInvalid = true;
          this.step = 'certificate-instructions';
          this.cdr.markForCheck();
          return;
        }

        this.qrCodeUrl = validation.qrCodeUrl;
        this.safeQrCodeUrl = validation.qrCodeUrl
          ? this.sanitizer.bypassSecurityTrustResourceUrl(validation.qrCodeUrl)
          : null;
        this.redirectsToLogin = false;
        this.step = 'action-required';
        this.cdr.markForCheck();
      },
      () => this.startErrorCountdown(),
    );
  }

  public cancel(): void {
    this.close$.emit();

    if (this.blockWorkspaceOnCancel) {
      this.logoutService.logout();
    }
  }

  public goBack(): void {
    this.close$.emit();
    this.navigateBack();
  }

  public onVidaasFrameLoad(event: Event): void {
    const frame = event.target as HTMLIFrameElement;

    try {
      const frameUrl = frame.contentWindow?.location?.href;
      if (!frameUrl) {
        return;
      }

      const code = extractQueryParam(frameUrl, 'code');
      if (code) {
        this.confirmValidation(code);
      }
    } catch {}
  }

  private confirmValidation(code: string): void {
    if (!this.doctorId) {
      this.startErrorCountdown();
      return;
    }

    this.step = 'processing';
    this.cdr.markForCheck();

    this.ptmSignerApiProviderService.validateDoctorQrCode(this.doctorId, code).subscribe(
      (result) => {
        if (result.signerChecked) {
          this.close$.emit();
        } else {
          this.startErrorCountdown();
        }
      },
      () => this.startErrorCountdown(),
    );
  }

  private startErrorCountdown(): void {
    this.step = 'error';
    this.countdown = ERROR_COUNTDOWN_SECONDS;
    this.cdr.markForCheck();

    this.countdownIntervalId = window.setInterval(() => {
      this.countdown -= 1;

      if (this.countdown <= 0) {
        this.stopCountdown();
        this.close$.emit();
        this.navigateBack();
      }

      this.cdr.markForCheck();
    }, 1000);
  }

  private navigateBack(): void {
    if (this.redirectsToLogin) {
      this.logoutService.logout();
      return;
    }

    this.router.navigateByUrl(RoutesBuilderService.SESSION_BY_DOCTOR.liveSessions());
  }

  private stopCountdown(): void {
    if (this.countdownIntervalId !== null) {
      clearInterval(this.countdownIntervalId);
      this.countdownIntervalId = null;
    }
  }
}
